import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { admins } from "../../Constants";
import { getAdminUsers, AdminUser } from "../../services/AccountService";
import Loading from "../../components/Loading";

type Segment = "all" | "donator" | "beta" | "premium" | "admin";

const SEGMENTS: { key: Segment; label: string }[] = [
    { key: "all", label: "All" },
    { key: "donator", label: "Donators" },
    { key: "beta", label: "Beta" },
    { key: "premium", label: "Premium" },
    { key: "admin", label: "Admins" },
];

const card: React.CSSProperties = {
    background: "linear-gradient(135deg, rgba(26,26,26,0.95) 0%, rgba(37,37,37,0.95) 100%)",
    border: "1px solid rgba(255,102,0,0.15)",
    borderRadius: 12,
    padding: "16px 20px",
    minWidth: 120,
    textAlign: "center",
};

const yesBadge: React.CSSProperties = {
    background: "rgba(74,222,128,0.2)", color: "#4ade80",
    borderRadius: 12, padding: "2px 10px", fontSize: "0.72rem", fontWeight: 700,
};
const noDash: React.CSSProperties = { color: "rgba(255,255,255,0.3)" };

const AdminUsersPage: React.FC = () => {
    const { user, authLoaded } = useContext(AuthContext);
    const isAdmin = !!user && admins.includes(user.steamID);

    const [users, setUsers] = useState<AdminUser[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [segment, setSegment] = useState<Segment>("all");
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (!isAdmin) return;
        getAdminUsers()
            .then(setUsers)
            .catch(() => setError("Could not load users."));
    }, [isAdmin]);

    const counts = useMemo(() => ({
        total: users?.length ?? 0,
        donator: users?.filter(u => u.donator).length ?? 0,
        beta: users?.filter(u => u.beta).length ?? 0,
        premium: users?.filter(u => u.premium).length ?? 0,
        admin: users?.filter(u => u.admin).length ?? 0,
    }), [users]);

    const filtered = useMemo(() => {
        if (!users) return [];
        const q = search.trim().toLowerCase();
        return users.filter(u => {
            if (segment === "donator" && !u.donator) return false;
            if (segment === "beta" && !u.beta) return false;
            if (segment === "premium" && !u.premium) return false;
            if (segment === "admin" && !u.admin) return false;
            if (q && !(u.playerName?.toLowerCase().includes(q) || u.steamId.includes(q))) return false;
            return true;
        });
    }, [users, segment, search]);

    if (!authLoaded) return <div className="text-center" style={{ paddingTop: 60 }}><Loading /></div>;
    if (!isAdmin) return <div className="container mt-4"><div className="alert alert-danger">Admins only.</div></div>;
    if (error) return <div className="container mt-4"><div className="alert alert-danger">{error}</div></div>;
    if (!users) return <div className="text-center" style={{ paddingTop: 60 }}><Loading /></div>;

    return (
        <div className="container mt-4" style={{ color: "var(--text-primary)" }}>
            <h1 style={{ color: "#e2e8f0", fontWeight: 700 }}>User Admin</h1>
            <p style={{ color: "rgba(255,255,255,0.6)" }}>Overview and search of all registered users.</p>

            {/* Summary */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "20px 0" }}>
                {[
                    { label: "Total", value: counts.total, color: "#e2e8f0" },
                    { label: "Donators", value: counts.donator, color: "#ff9933" },
                    { label: "Beta", value: counts.beta, color: "#60a5fa" },
                    { label: "Premium", value: counts.premium, color: "#4ade80" },
                    { label: "Admins", value: counts.admin, color: "#f87171" },
                ].map(s => (
                    <div key={s.label} style={card}>
                        <div style={{ fontSize: "1.6rem", fontWeight: 700, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.55)" }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Controls */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
                <div className="btn-group btn-group-sm">
                    {SEGMENTS.map(s => (
                        <button
                            key={s.key}
                            className={`btn ${segment === s.key ? "btn-warning text-dark" : "btn-outline-secondary"}`}
                            onClick={() => setSegment(s.key)}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
                <input
                    placeholder="Search name or SteamID…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{
                        flex: "1 1 240px", maxWidth: 360,
                        background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,102,0,0.2)",
                        borderRadius: 6, color: "rgba(255,255,255,0.9)", padding: "8px 12px",
                    }}
                />
                <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem" }}>{filtered.length} shown</span>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto", background: "linear-gradient(135deg,#1a1a1a 0%,#252525 100%)", border: "1px solid rgba(255,102,0,0.1)", borderRadius: 12 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", color: "#e2e8f0" }}>
                    <thead style={{ background: "linear-gradient(135deg,#2a2a2a 0%,#333 100%)", borderBottom: "2px solid rgba(255,102,0,0.15)" }}>
                        <tr>
                            {["Player", "SteamID", "Donator", "Beta", "Premium", "Admin"].map(h => (
                                <th key={h} style={{ padding: "12px 16px", textAlign: "left", textTransform: "uppercase", fontSize: 13, letterSpacing: "0.5px", color: "rgba(255,255,255,0.9)" }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((u, i) => (
                            <tr key={u.steamId} style={{ background: i % 2 ? "rgba(36,36,36,0.5)" : "rgba(42,42,42,0.5)" }}>
                                <td style={{ padding: "10px 16px" }}>
                                    <Link to={`/player/${u.steamId}`} style={{ color: "#ff9933", fontWeight: 600, textDecoration: "none" }}>
                                        {u.playerName || "—"}
                                    </Link>
                                </td>
                                <td style={{ padding: "10px 16px", color: "rgba(255,255,255,0.6)", fontSize: 13 }}>{u.steamId}</td>
                                <td style={{ padding: "10px 16px" }}>{u.donator ? <span style={yesBadge}>YES</span> : <span style={noDash}>—</span>}</td>
                                <td style={{ padding: "10px 16px" }}>{u.beta ? <span style={yesBadge}>YES</span> : <span style={noDash}>—</span>}</td>
                                <td style={{ padding: "10px 16px" }}>{u.premium ? <span style={yesBadge}>YES</span> : <span style={noDash}>—</span>}</td>
                                <td style={{ padding: "10px 16px" }}>{u.admin ? <span style={yesBadge}>YES</span> : <span style={noDash}>—</span>}</td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: "rgba(255,255,255,0.5)" }}>No users match.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminUsersPage;
